/* Gói thẻ liệu trình bán trên phiếu báo giá — danh mục gói đang bán trên API; lỗi / rỗng thì dùng gói mẫu của module Thẻ liệu trình */
import { useEffect, useState } from 'react';
import { getTreatmentPackages } from '../../api/TreatmentV2';
import useTreatmentStore from '../Treatment/store';
import { serviceOf } from '../Treatment/data';
import { uid, vnd, type CardInfo, type QLine } from './logic';

export interface CardPkg { id: string; name: string; group: string; price: number; card: CardInfo }

const KIND_GROUP = { session: 'Gói theo buổi', value: 'Thẻ trả trước' } as const;

let cache: CardPkg[] | null = null;
let pending: Promise<CardPkg[]> | null = null;
/** Tài khoản không có quyền đọc danh mục gói: không thay bằng gói mẫu, vì giá mẫu có thể đi vào báo giá khách thật */
let forbidden = false;

/** Gói mẫu (localStorage của module Thẻ liệu trình) — đang bán */
const localPkgs = (): CardPkg[] => useTreatmentStore.getState().packages.filter((p) => p.active).map((p) => ({
  id: `pkg-${p.id}`, name: p.name, group: p.category, price: p.price,
  card: {
    code: p.code, kind: p.kind, validityMonths: p.validityMonths, faceValue: p.kind === 'value' ? p.faceValue : undefined,
    items: p.items.map((x) => ({ name: serviceOf(x.serviceId)?.name ?? x.serviceId, sessions: x.sessions, bonus: x.role === 'bonus' || undefined })),
  },
}));

const load = () => {
  pending ??= getTreatmentPackages({ is_active: true, per_page: 200 })
    .then((r) => { forbidden = r.status === 403; return r; })
    .then((r) => (r.ok && r.data ? r.data.items : []).filter((p) => p.is_active).map((p): CardPkg => ({
      id: `pkg-api-${p.id}`, name: p.name.trim(), group: KIND_GROUP[p.kind], price: p.current_price?.price ?? p.price,
      card: {
        code: p.code, kind: p.kind, validityMonths: p.validity_months, faceValue: p.kind === 'value' ? p.face_value : undefined, apiId: p.id,
        items: p.items.map((x) => ({ name: x.service_name ?? `Dịch vụ #${x.service_id}`, sessions: x.sessions, bonus: x.role === 'bonus' || undefined })),
      },
    })))
    .catch(() => [] as CardPkg[])
    .then((list) => { cache = list.length || forbidden ? list : localPkgs(); return cache; });
  return pending;
};

/** Tải danh mục gói, không bao giờ lỗi */
export const loadCardPackages = () => load();

/** Chữ hiện khi ô Thêm thẻ liệu trình không có gói */
export const cardEmptyText = () => (forbidden ? 'Tài khoản chưa có quyền xem gói thẻ liệu trình' : 'Chưa có gói đang bán');

/** Gói đang bán (null = đang tải) */
export function useCardPackages(): CardPkg[] | null {
  const [list, setList] = useState(cache);
  useEffect(() => {
    let alive = true;
    if (!cache) load().then((x) => { if (alive) setList(x); });
    return () => { alive = false; };
  }, []);
  return list;
}

/** Dòng phiếu cho một thẻ: đơn vị "thẻ", giá gói */
export const cardLine = (p: CardPkg): QLine => ({ id: uid(), svcId: p.id, name: p.name, unit: 'thẻ', qty: 1, price: p.price, card: p.card });

/** Lựa chọn trong ô "Thêm thẻ liệu trình", gom theo nhóm gói; priced = false cho bác sĩ (không thấy giá) */
export const cardOptions = (list: CardPkg[], priced: boolean) =>
  Object.entries(list.reduce<Record<string, CardPkg[]>>((m, p) => { (m[p.group] ??= []).push(p); return m; }, {}))
    .map(([group, ps]) => ({ label: group, options: ps.map((p) => ({ value: p.id, label: priced ? `${p.name} · ${vnd(p.price)}` : p.name })) }));
